/**
 * `/me` — the volunteer self-service dashboard (M12b).
 *
 * Consumes `GET /me` (`ApiClient.getMe`) and renders:
 *   - a header with preferred_name / full_name + sign-out action
 *   - a status banner (green/amber/red) with plain-English copy that
 *     comes straight from the server so the rules live in one place
 *   - the compliance checklist — one row per applicable requirement,
 *     each row carrying its own status badge, "what to do next" block,
 *     and an inline-upload widget on `affordance_kind === 'upload_evidence'`.
 *     Volunteer-attestation rows render inline alongside the checklist
 *     items with their configured confirm button.
 *   - the signed-policies list (one row per recorded acknowledgement)
 *
 * The inline-upload widget PUTs the chosen file at the server-issued
 * pre-signed URL, then POSTs `/me/evidence/confirm` so the API can
 * hash + validate + transition the record to `submitted`. On success we
 * re-fetch `/me` so the row re-renders with the fresh
 * "Awaiting verification" badge without a full page reload.
 *
 * Session handling: a 401 from any `/me` call means the cookie is gone
 * or expired — we route back to `/sign-in` immediately. Sign-out is a
 * POST `/volunteer/sign-out` + navigate.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client.js';
import type { ApiClient } from '../api/client.js';
import { ErrorBanner } from '../components/ErrorBanner.js';
import { renderMarkdownExplainer } from './markdown-explainer.js';
import type {
  AckSubmitConfirmations,
  AuStateCode,
  MeAckStartResponse,
  MeAttestationRow,
  MeDashboardResponse,
  MePendingAckRow,
  MeProfileUpdateRequest,
  MeSelfPerson,
  TrainingChecklistRow,
  MeAcknowledgementHistoryEntry,
} from '@isms/shared';

const AU_STATE_CODES: readonly AuStateCode[] = [
  'ACT',
  'NSW',
  'QLD',
  'SA',
  'TAS',
  'VIC',
  'WA',
  'NT',
];

export interface MePageProps {
  client: ApiClient;
  /** Where to send the volunteer after sign-out / on 401. Defaults to
   *  `/sign-in` — tests override to decouple from the router setup. */
  signInPath?: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; error: ApiError | string }
  | { kind: 'ready'; data: MeDashboardResponse };

export function MePage({ client, signInPath = '/sign-in' }: MePageProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [signOutPending, setSignOutPending] = useState(false);
  const navigate = useNavigate();

  const redirectToSignIn = useCallback(() => {
    navigate(signInPath, { replace: true });
  }, [navigate, signInPath]);

  const load = useCallback(async () => {
    try {
      const data = await client.getMe();
      setState({ kind: 'ready', data });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        redirectToSignIn();
        return;
      }
      setState({
        kind: 'error',
        error:
          err instanceof ApiError
            ? err
            : "We couldn't load your dashboard. Please check your connection and try again.",
      });
    }
  }, [client, redirectToSignIn]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleSignOut(): Promise<void> {
    setSignOutPending(true);
    try {
      await client.volunteerSignOut();
    } catch {
      // Even if sign-out fails server-side (stale cookie, network blip),
      // sending the user to the sign-in page is the safe default.
    } finally {
      redirectToSignIn();
    }
  }

  if (state.kind === 'loading') {
    return (
      <main className="me-page" aria-busy="true">
        <p className="me-loading">Loading your compliance dashboard…</p>
      </main>
    );
  }

  if (state.kind === 'error') {
    return (
      <main className="me-page">
        <div className="me-error">
          <ErrorBanner error={state.error} />
          <button type="button" onClick={() => void load()}>
            Retry
          </button>
        </div>
      </main>
    );
  }

  const {
    person,
    status_banner,
    training_checklist,
    attestations,
    pending_acknowledgements,
    acknowledgement_history,
  } = state.data;
  const displayName = person.preferred_name ?? person.full_name;

  return (
    <main className="me-page" aria-labelledby="me-heading">
      <header className="me-header">
        <div>
          <h1 id="me-heading">Hi {displayName}</h1>
          <p className="me-header-email">{person.contact_email}</p>
          {person.role_tags.length > 0 ? (
            <ul className="me-role-tags" aria-label="Role tags">
              {person.role_tags.map((tag) => (
                <li key={tag}>{tag}</li>
              ))}
            </ul>
          ) : null}
        </div>
        <button
          type="button"
          className="me-signout-button"
          onClick={() => void handleSignOut()}
          disabled={signOutPending}
        >
          {signOutPending ? 'Signing out…' : 'Sign out'}
        </button>
      </header>

      <section
        className={`me-status-banner me-status-banner--${status_banner.kind}`}
        role="status"
        aria-live="polite"
      >
        {status_banner.message}
      </section>

      <EditProfilePanel
        person={person}
        client={client}
        pendingAttestations={attestations.filter((a) => a.state === 'pending')}
        onUpdated={(updated) => {
          setState({ kind: 'ready', data: { ...state.data, person: updated } });
        }}
      />

      <section className="me-checklist" aria-labelledby="me-checklist-heading">
        <h2 id="me-checklist-heading">Compliance checklist</h2>
        {training_checklist.length === 0 && attestations.length === 0 ? (
          <p className="me-empty">No compliance items apply to you yet.</p>
        ) : (
          <ul className="me-checklist-list">
            {training_checklist.map((row) => {
              const pendingAck =
                pending_acknowledgements.find(
                  (p) => String(p.compliance_record_id) === String(row.current_record_id),
                ) ?? null;
              return (
                <ChecklistRow
                  key={`req-${String(row.requirement_id)}`}
                  row={row}
                  client={client}
                  pendingAck={pendingAck}
                  onUploadComplete={() => void load()}
                  onSigned={() => void load()}
                />
              );
            })}
            {attestations.map((row) => (
              <AttestationRow
                key={`att-${String(row.requirement_id)}`}
                row={row}
                client={client}
                onConfirmed={() => void load()}
              />
            ))}
          </ul>
        )}
      </section>

      <section className="me-ack-history" aria-labelledby="me-ack-history-heading">
        <h2 id="me-ack-history-heading">Signed policies</h2>
        {acknowledgement_history.length === 0 ? (
          <p className="me-empty">You haven't signed any policies yet.</p>
        ) : (
          <ul className="me-ack-list">
            {acknowledgement_history.map((entry) => (
              <AcknowledgementHistoryItem key={entry.signature_id} entry={entry} />
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}

interface EditProfilePanelProps {
  person: MeSelfPerson;
  client: ApiClient;
  /** Attestation rows currently in `pending` state. After a successful
   *  save, the panel surfaces a non-blocking nudge reminding the
   *  volunteer that saving the form does NOT satisfy the attestation —
   *  they still need to click the configured confirm button below. */
  pendingAttestations: MeAttestationRow[];
  onUpdated: (updated: MeSelfPerson) => void;
}

interface ProfileFormFields {
  title: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  preferred_name: string;
  mobile_phone: string;
  address_line_1: string;
  address_line_2: string;
  address_suburb: string;
  address_state: AuStateCode | '';
  address_postcode: string;
}

function projectFormFields(person: MeSelfPerson): ProfileFormFields {
  return {
    title: person.title ?? '',
    first_name: person.first_name,
    middle_name: person.middle_name ?? '',
    last_name: person.last_name,
    preferred_name: person.preferred_name ?? '',
    mobile_phone: person.mobile_phone ?? '',
    address_line_1: person.address_line_1 ?? '',
    address_line_2: person.address_line_2 ?? '',
    address_suburb: person.address_suburb ?? '',
    address_state: person.address_state ?? '',
    address_postcode: person.address_postcode ?? '',
  };
}

/**
 * Diff the form against the person row on the wire — only fields that
 * actually changed land in the patch. Empty strings on nullable fields
 * mean "clear", so they map to `null`. Empty strings on required name
 * parts (`first_name`, `last_name`) are filtered out — the server-side
 * zod schema would reject them, but it's friendlier to surface that as
 * a client-side validation error before the request goes out.
 */
function diffProfilePatch(
  person: MeSelfPerson,
  fields: ProfileFormFields,
): { patch: MeProfileUpdateRequest; nameError: string | null } {
  const patch: MeProfileUpdateRequest = {};
  const nullable = (raw: string): string | null => (raw.trim() === '' ? null : raw.trim());

  if (fields.title !== (person.title ?? '')) patch.title = nullable(fields.title);
  if (fields.middle_name !== (person.middle_name ?? ''))
    patch.middle_name = nullable(fields.middle_name);
  if (fields.preferred_name !== (person.preferred_name ?? ''))
    patch.preferred_name = nullable(fields.preferred_name);
  if (fields.mobile_phone !== (person.mobile_phone ?? ''))
    patch.mobile_phone = nullable(fields.mobile_phone);
  if (fields.address_line_1 !== (person.address_line_1 ?? ''))
    patch.address_line_1 = nullable(fields.address_line_1);
  if (fields.address_line_2 !== (person.address_line_2 ?? ''))
    patch.address_line_2 = nullable(fields.address_line_2);
  if (fields.address_suburb !== (person.address_suburb ?? ''))
    patch.address_suburb = nullable(fields.address_suburb);
  if (fields.address_postcode !== (person.address_postcode ?? ''))
    patch.address_postcode = nullable(fields.address_postcode);

  // address_state is a closed enum — '' on the form means "cleared".
  if (fields.address_state !== (person.address_state ?? ''))
    patch.address_state = fields.address_state === '' ? null : fields.address_state;

  // Required name parts: trim, validate, only patch when changed and
  // non-empty.
  const trimmedFirst = fields.first_name.trim();
  const trimmedLast = fields.last_name.trim();
  const firstChanged = trimmedFirst !== person.first_name;
  const lastChanged = trimmedLast !== person.last_name;
  let nameError: string | null = null;
  if (firstChanged) {
    if (trimmedFirst === '') nameError = 'First name cannot be empty.';
    else patch.first_name = trimmedFirst;
  }
  if (lastChanged) {
    if (trimmedLast === '') nameError = nameError ?? 'Last name cannot be empty.';
    else patch.last_name = trimmedLast;
  }

  return { patch, nameError };
}

type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'error'; error: ApiError | string };

export function EditProfilePanel({
  person,
  client,
  pendingAttestations,
  onUpdated,
}: EditProfilePanelProps): JSX.Element {
  const [fields, setFields] = useState<ProfileFormFields>(() => projectFormFields(person));
  const [saveState, setSaveState] = useState<SaveState>({ kind: 'idle' });

  // Keep the form snapshot in sync if a *different* person is loaded
  // (in practice that only happens when the parent redirects after a
  // session swap). We deliberately do NOT reset on every `person`
  // reference change — that would clobber the "Saved." status the
  // moment our own PATCH writes back into parent state.
  const personKey = String(person.person_id);
  const personRef = useRef(person);
  personRef.current = person;
  useEffect(() => {
    setFields(projectFormFields(personRef.current));
    setSaveState({ kind: 'idle' });
  }, [personKey]);

  function handleChange<K extends keyof ProfileFormFields>(
    field: K,
    value: ProfileFormFields[K],
  ): void {
    setFields((prev) => ({ ...prev, [field]: value }));
    if (saveState.kind === 'saved' || saveState.kind === 'error') {
      setSaveState({ kind: 'idle' });
    }
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const { patch, nameError } = diffProfilePatch(person, fields);
    if (nameError) {
      setSaveState({ kind: 'error', error: nameError });
      return;
    }
    if (Object.keys(patch).length === 0) {
      setSaveState({ kind: 'error', error: 'Update at least one field before saving.' });
      return;
    }

    setSaveState({ kind: 'saving' });
    try {
      const result = await client.patchMeProfile(patch);
      onUpdated(result.person);
      setSaveState({ kind: 'saved' });
    } catch (err) {
      const errorValue: ApiError | string =
        err instanceof ApiError
          ? err
          : err instanceof Error
            ? err.message
            : "We couldn't save your changes. Please try again.";
      setSaveState({ kind: 'error', error: errorValue });
    }
  }

  const isSaving = saveState.kind === 'saving';
  const formId = `me-profile-form`;

  return (
    <section className="me-profile" aria-labelledby="me-profile-heading">
      <h2 id="me-profile-heading">Your details</h2>
      <p className="me-profile-intro">
        Update your contact details, address, or how your name is recorded. Your school admin keeps
        sight of your email and date of birth — please contact them to change those.
      </p>
      <form
        id={formId}
        className="me-profile-form"
        onSubmit={(e) => void handleSubmit(e)}
        noValidate
      >
        <fieldset disabled={isSaving}>
          <legend className="me-profile-legend">Name</legend>
          <label className="me-profile-field me-profile-field--full me-profile-field--narrow">
            <span>Title</span>
            <input
              type="text"
              value={fields.title}
              onChange={(e) => handleChange('title', e.target.value)}
              autoComplete="honorific-prefix"
              maxLength={40}
            />
          </label>
          <label className="me-profile-field">
            <span>First name</span>
            <input
              type="text"
              value={fields.first_name}
              onChange={(e) => handleChange('first_name', e.target.value)}
              autoComplete="given-name"
              required
              aria-required="true"
            />
          </label>
          <label className="me-profile-field">
            <span>Middle name</span>
            <input
              type="text"
              value={fields.middle_name}
              onChange={(e) => handleChange('middle_name', e.target.value)}
              autoComplete="additional-name"
            />
          </label>
          <label className="me-profile-field me-profile-field--full">
            <span>Last name</span>
            <input
              type="text"
              value={fields.last_name}
              onChange={(e) => handleChange('last_name', e.target.value)}
              autoComplete="family-name"
              required
              aria-required="true"
            />
          </label>
          <label className="me-profile-field me-profile-field--full">
            <span>Preferred name</span>
            <input
              type="text"
              value={fields.preferred_name}
              onChange={(e) => handleChange('preferred_name', e.target.value)}
              autoComplete="nickname"
            />
          </label>
        </fieldset>

        <fieldset disabled={isSaving}>
          <legend className="me-profile-legend">Contact</legend>
          <div className="me-profile-field me-profile-field--readonly">
            <label htmlFor="me-profile-email">Email</label>
            <input
              id="me-profile-email"
              type="email"
              value={person.contact_email}
              readOnly
              aria-readonly="true"
              autoComplete="email"
            />
            <small>Contact your school admin to change your email address.</small>
          </div>
          <label className="me-profile-field">
            <span>Phone</span>
            <input
              type="tel"
              value={fields.mobile_phone}
              onChange={(e) => handleChange('mobile_phone', e.target.value)}
              autoComplete="tel"
              maxLength={40}
            />
          </label>
        </fieldset>

        <fieldset disabled={isSaving}>
          <legend className="me-profile-legend">Address</legend>
          <label className="me-profile-field">
            <span>Address line 1</span>
            <input
              type="text"
              value={fields.address_line_1}
              onChange={(e) => handleChange('address_line_1', e.target.value)}
              autoComplete="address-line1"
              maxLength={200}
            />
          </label>
          <label className="me-profile-field">
            <span>Address line 2</span>
            <input
              type="text"
              value={fields.address_line_2}
              onChange={(e) => handleChange('address_line_2', e.target.value)}
              autoComplete="address-line2"
              maxLength={200}
            />
          </label>
          <label className="me-profile-field">
            <span>Suburb</span>
            <input
              type="text"
              value={fields.address_suburb}
              onChange={(e) => handleChange('address_suburb', e.target.value)}
              autoComplete="address-level2"
              maxLength={100}
            />
          </label>
          <label className="me-profile-field me-profile-field--narrow">
            <span>State</span>
            <select
              value={fields.address_state}
              onChange={(e) => handleChange('address_state', e.target.value as AuStateCode | '')}
              autoComplete="address-level1"
            >
              <option value="">—</option>
              {AU_STATE_CODES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </label>
          <label className="me-profile-field me-profile-field--narrow">
            <span>Postcode</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{4}"
              value={fields.address_postcode}
              onChange={(e) => handleChange('address_postcode', e.target.value)}
              autoComplete="postal-code"
              maxLength={4}
            />
          </label>
        </fieldset>

        <div className="me-profile-actions">
          <button type="submit" className="me-profile-save" disabled={isSaving}>
            {isSaving ? 'Saving…' : 'Save changes'}
          </button>
          {saveState.kind === 'saved' ? (
            <p className="me-profile-status me-profile-status--saved" role="status">
              Saved.
            </p>
          ) : null}
          {saveState.kind === 'saved' && pendingAttestations.length > 0 ? (
            <p className="me-profile-attestation-nudge" role="status">
              Don&rsquo;t forget to click{' '}
              <strong>{pendingAttestations[0]!.confirm_button_label}</strong> below to finish.
            </p>
          ) : null}
          {saveState.kind === 'error' ? <ErrorBanner error={saveState.error} /> : null}
        </div>
      </form>
    </section>
  );
}

interface ChecklistRowProps {
  row: TrainingChecklistRow;
  client: ApiClient;
  /** When the row is sign-pattern and the volunteer owes a signature
   *  on a linked policy, this is the matching pending row. The Sign
   *  affordance renders inline against it instead of in a separate
   *  Pending acknowledgements section. */
  pendingAck: MePendingAckRow | null;
  onUploadComplete: () => void;
  onSigned: () => void;
}

function ChecklistRow({
  row,
  client,
  pendingAck,
  onUploadComplete,
  onSigned,
}: ChecklistRowProps): JSX.Element {
  return (
    <li className={`me-checklist-row me-checklist-row--${row.status}`}>
      <div className="me-checklist-row-header">
        <h3 className="me-checklist-row-title">{row.name}</h3>
        <span className={`me-badge me-badge--${row.status}`}>{row.status_label}</span>
      </div>
      <p className="me-checklist-row-description">{row.short_description}</p>

      {row.verified_details ? <VerifiedDetails details={row.verified_details} /> : null}

      {row.current_evidence ? (
        <p className="me-checklist-row-evidence">
          Your file:{' '}
          <a
            href={row.current_evidence.download_url}
            target="_blank"
            rel="noreferrer noopener"
          >
            {row.current_evidence.filename}
          </a>{' '}
          <span className="me-checklist-row-evidence-meta">
            (uploaded {formatUploadedAt(row.current_evidence.uploaded_at)})
          </span>
        </p>
      ) : null}

      {row.what_to_do_next ? (
        <div className="me-what-to-do">
          <h4>What to do next</h4>
          {row.what_to_do_next.external_link ? (
            <p>
              <a
                href={row.what_to_do_next.external_link.url}
                target="_blank"
                rel="noreferrer noopener"
              >
                {row.what_to_do_next.external_link.label}
              </a>
            </p>
          ) : null}
          <ol className="me-instructions">
            {row.what_to_do_next.instructions.map((step) => (
              <li key={step.step_number}>{step.text}</li>
            ))}
          </ol>
          {row.what_to_do_next.typical_time ? (
            <p className="me-typical-time">
              <strong>Typical time:</strong> {row.what_to_do_next.typical_time}
            </p>
          ) : null}
        </div>
      ) : null}

      {row.affordance_kind === 'upload_evidence' && row.current_record_id ? (
        <UploadWidget
          recordId={row.current_record_id}
          primaryActionLabel={row.what_to_do_next?.primary_action_label ?? 'Upload evidence'}
          client={client}
          onUploadComplete={onUploadComplete}
        />
      ) : null}

      {row.affordance_kind === 'visit_acknowledgement' ? (
        pendingAck ? (
          <PendingAckInline row={pendingAck} client={client} onSigned={onSigned} />
        ) : (
          <p className="me-ack-hint">
            Awaiting a fresh policy version — there's nothing to sign right now.
          </p>
        )
      ) : null}
    </li>
  );
}

interface VerifiedDetailsProps {
  details: NonNullable<TrainingChecklistRow['verified_details']>;
}

function formatUploadedAt(value: string | Date): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function VerifiedDetails({ details }: VerifiedDetailsProps): JSX.Element {
  const verifiedOnLabel = new Date(details.verified_on).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const expiryLabel = details.expiry_date
    ? new Date(details.expiry_date).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : null;
  return (
    <dl className="me-verified-details">
      <div>
        <dt>Verified on</dt>
        <dd>{verifiedOnLabel}</dd>
      </div>
      {expiryLabel ? (
        <div>
          <dt>Expires</dt>
          <dd>{expiryLabel}</dd>
        </div>
      ) : null}
      {details.verified_by_display ? (
        <div>
          <dt>Verified by</dt>
          <dd>{details.verified_by_display}</dd>
        </div>
      ) : null}
      {details.evidence_download_url ? (
        <div>
          <dt>Your file</dt>
          <dd>
            <a href={details.evidence_download_url} target="_blank" rel="noreferrer noopener">
              Download a copy
            </a>
          </dd>
        </div>
      ) : null}
    </dl>
  );
}

interface UploadWidgetProps {
  recordId: string | number | bigint;
  primaryActionLabel: string;
  client: ApiClient;
  onUploadComplete: () => void;
}

type UploadState =
  | { kind: 'idle' }
  | { kind: 'uploading'; filename: string }
  | { kind: 'just-uploaded'; filename: string }
  | { kind: 'error'; error: ApiError | string };

function UploadWidget({
  recordId,
  primaryActionLabel,
  client,
  onUploadComplete,
}: UploadWidgetProps): JSX.Element {
  const [state, setState] = useState<UploadState>({ kind: 'idle' });
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleFile(file: File): Promise<void> {
    setState({ kind: 'uploading', filename: file.name });
    try {
      const presigned = await client.requestMeEvidenceUpload({
        compliance_record_id: recordId,
        filename: file.name,
        mime_type: file.type || 'application/octet-stream',
        size_bytes: file.size,
      });
      await client.putEvidenceFile(presigned.upload_url, file);
      await client.confirmMeEvidence({
        compliance_record_id: recordId,
        storage_key: presigned.storage_key,
      });
      // Land in `just-uploaded` so the row immediately shows
      // confirmation. The /me reload that follows replaces this with
      // the server's `current_evidence` block (filename + presigned
      // download link); during the in-flight reload, the volunteer
      // already sees something acknowledging their action.
      setState({ kind: 'just-uploaded', filename: file.name });
      if (fileInputRef.current) fileInputRef.current.value = '';
      onUploadComplete();
    } catch (err) {
      setState({
        kind: 'error',
        error:
          err instanceof ApiError
            ? err
            : err instanceof Error
              ? err.message
              : 'Upload failed. Please try again.',
      });
    }
  }

  function onChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  }

  const isUploading = state.kind === 'uploading';

  return (
    <div className="me-upload-widget">
      <label className="me-upload-label">
        <span>{primaryActionLabel}</span>
        <input
          type="file"
          accept="application/pdf,image/png,image/jpeg"
          onChange={onChange}
          disabled={isUploading}
          ref={fileInputRef}
          aria-label={primaryActionLabel}
        />
      </label>
      <p className="me-upload-hint">PDF, PNG, or JPEG — up to 20 MB.</p>
      {isUploading ? (
        <p className="me-upload-progress" role="status">
          Uploading {state.filename}…
        </p>
      ) : null}
      {state.kind === 'just-uploaded' ? (
        <p className="me-upload-just-uploaded" role="status">
          ✓ Uploaded <strong>{state.filename}</strong>. Awaiting verification.
        </p>
      ) : null}
      {state.kind === 'error' ? <ErrorBanner error={state.error} /> : null}
    </div>
  );
}

interface AckHistoryItemProps {
  entry: MeAcknowledgementHistoryEntry;
}

function AcknowledgementHistoryItem({ entry }: AckHistoryItemProps): JSX.Element {
  const submittedLabel = new Date(entry.submitted_at_utc).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return (
    <li className="me-ack-row">
      <div>
        <p className="me-ack-title">
          <strong>{entry.document_title}</strong> (v{entry.version})
        </p>
        <p className="me-ack-meta">Acknowledged {submittedLabel}</p>
      </div>
      {entry.receipt_download_url ? (
        <a
          href={entry.receipt_download_url}
          target="_blank"
          rel="noreferrer noopener"
          className="me-ack-receipt-link"
        >
          Download receipt
        </a>
      ) : null}
    </li>
  );
}

interface AttestationRowProps {
  row: MeAttestationRow;
  client: ApiClient;
  onConfirmed: () => void;
}

type AttestationConfirmState =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'error'; error: ApiError | string };

function AttestationRow({ row, client, onConfirmed }: AttestationRowProps): JSX.Element {
  const [state, setState] = useState<AttestationConfirmState>({ kind: 'idle' });

  const isVerifiedCurrent = row.state === 'verified';
  const expiryLabel = row.expiry_date
    ? new Date(row.expiry_date).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : null;

  async function handleConfirm(): Promise<void> {
    setState({ kind: 'pending' });
    try {
      await client.confirmMeAttestation(String(row.requirement_id));
      onConfirmed();
    } catch (err) {
      const errorValue: ApiError | string =
        err instanceof ApiError
          ? err
          : err instanceof Error
            ? err.message
            : "We couldn't record your confirmation. Please try again.";
      setState({ kind: 'error', error: errorValue });
    }
  }

  return (
    <li className={`me-checklist-row me-checklist-row--${row.state}`}>
      <div className="me-checklist-row-header">
        <h3 className="me-checklist-row-title">{row.name}</h3>
        <span className={`me-badge me-badge--${row.state}`}>
          {row.state === 'pending'
            ? 'Action required'
            : row.state === 'expiring_soon'
              ? expiryLabel
                ? `Expires ${expiryLabel}`
                : 'Expiring soon'
              : expiryLabel
                ? `Next due ${expiryLabel}`
                : 'Confirmed'}
        </span>
      </div>
      <div className="me-checklist-row-description">
        {renderMarkdownExplainer(row.markdown_explainer)}
      </div>
      {isVerifiedCurrent ? null : (
        <button
          type="button"
          className="me-attestation-confirm"
          onClick={() => void handleConfirm()}
          disabled={state.kind === 'pending'}
        >
          {state.kind === 'pending' ? 'Confirming…' : row.confirm_button_label}
        </button>
      )}
      {state.kind === 'error' ? <ErrorBanner error={state.error} /> : null}
    </li>
  );
}

// ===========================================================================
// Pending acknowledgements pane (M13) — inline policy acknowledgement
// flow that replaces the per-delivery `/ack/:token` URL.
//
// One row per pending policy ack. Clicking Sign opens an inline modal-
// like sub-pane with the full read → type-name → step-up → confirm
// walk-through. The legal load is borne server-side: the email step-up
// code consumed in the same transaction as the signature row gives the
// per-event identification factor required by the Electronic Transactions
// Act 2000 (NSW).
// ===========================================================================

interface PendingAckInlineProps {
  row: MePendingAckRow;
  client: ApiClient;
  onSigned: () => void;
}

/**
 * Inline Sign affordance rendered against the matching sign-pattern
 * checklist row. Toggles between a primary "Sign" button and the
 * full read → type-name → step-up → confirm walk-through. Replaces
 * the old standalone Pending acknowledgements section so the volunteer
 * has one unified compliance checklist with the action surfaced where
 * the row lives.
 */
function PendingAckInline({ row, client, onSigned }: PendingAckInlineProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Drive the native `<dialog>` imperatively so React state stays the
  // single source of truth (close on Esc / backdrop click also flips
  // `open` back via the `onClose` event below).
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <div className="me-pending-ack-inline">
      <p className="me-pending-ack-version-line">
        {row.document_title} <span className="me-pending-ack-version">(v{row.version})</span>
      </p>
      <button
        type="button"
        className="me-pending-ack-open"
        onClick={() => setOpen(true)}
        data-testid={`me-pending-ack-sign-${row.compliance_record_id}`}
      >
        {row.reason === 'expired' ? 'Re-sign policy' : 'Read & sign policy'}
      </button>
      <dialog
        ref={dialogRef}
        className="me-pending-ack-dialog"
        onClose={() => setOpen(false)}
        onClick={(e) => {
          // Backdrop click — when the user clicks outside the inner card
          // the event target is the <dialog> itself rather than any
          // descendant. Close cleanly.
          if (e.target === dialogRef.current) setOpen(false);
        }}
      >
        {open ? (
          <PendingAckSignFlow
            row={row}
            client={client}
            onClose={() => setOpen(false)}
            onSigned={() => {
              setOpen(false);
              onSigned();
            }}
          />
        ) : null}
      </dialog>
    </div>
  );
}

interface PendingAckSignFlowProps {
  row: MePendingAckRow;
  client: ApiClient;
  onClose: () => void;
  onSigned: () => void;
}

type PendingAckStage =
  | { kind: 'loading' }
  | { kind: 'read'; page: MeAckStartResponse }
  | { kind: 'submitting'; page: MeAckStartResponse }
  | {
      kind: 'near-match';
      page: MeAckStartResponse;
      typedName: string;
      hint: string;
    }
  | {
      kind: 'enter-code';
      page: MeAckStartResponse;
      attemptId: string;
      expiresInSeconds: number;
    }
  | {
      kind: 'confirming';
      page: MeAckStartResponse;
      attemptId: string;
    }
  | { kind: 'mismatch' }
  | { kind: 'error'; error: ApiError | string };

function PendingAckSignFlow({
  row,
  client,
  onClose,
  onSigned,
}: PendingAckSignFlowProps): JSX.Element {
  const [stage, setStage] = useState<PendingAckStage>({ kind: 'loading' });
  const [typedName, setTypedName] = useState('');
  const [readToEnd, setReadToEnd] = useState(false);
  const [understandsLegal, setUnderstandsLegal] = useState(false);
  const [readVia, setReadVia] = useState<'inline_scroll' | 'external_viewer'>('inline_scroll');
  const [code, setCode] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      try {
        const page = await client.startMeAck({ compliance_record_id: row.compliance_record_id });
        if (cancelled) return;
        setTypedName(page.full_name_on_file);
        setStage({ kind: 'read', page });
      } catch (err) {
        if (cancelled) return;
        setStage({
          kind: 'error',
          error:
            err instanceof ApiError
              ? err
              : "We couldn't open the acknowledgement page. Please reload and try again.",
        });
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [client, row.compliance_record_id]);

  async function handleStepUp(page: MeAckStartResponse, nameOverride: boolean): Promise<void> {
    setStage({ kind: 'submitting', page });
    const confirmations: AckSubmitConfirmations = {
      read_via: readVia,
      scrolled_to_end: readToEnd,
      understands_legal_equivalence: understandsLegal,
      name_override: nameOverride,
    };
    try {
      const result = await client.stepUpMeAck({
        compliance_record_id: row.compliance_record_id,
        page_render_id: page.page_render_id,
        typed_name: typedName.trim(),
        confirmations,
        client_local_time: new Date().toISOString(),
      });
      setCode('');
      setStage({
        kind: 'enter-code',
        page,
        attemptId: result.attempt_id,
        expiresInSeconds: result.expires_in_seconds,
      });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'name_near_match_needs_override') {
        setStage({
          kind: 'near-match',
          page,
          typedName: typedName.trim(),
          hint: err.message,
        });
        return;
      }
      if (err instanceof ApiError && err.code === 'name_mismatch') {
        setStage({ kind: 'mismatch' });
        return;
      }
      setStage({
        kind: 'error',
        error:
          err instanceof ApiError
            ? err
            : "We couldn't send the confirmation code. Please try again.",
      });
    }
  }

  async function handleConfirm(page: MeAckStartResponse, attemptId: string): Promise<void> {
    setStage({ kind: 'confirming', page, attemptId });
    try {
      await client.confirmMeAck({ attempt_id: attemptId, code: code.trim() });
      onSigned();
    } catch (err) {
      // All confirm failures collapse to invalid_code on the server,
      // which the SPA can't disambiguate between "wrong code typed",
      // "code expired", or "second-tab-already-redeemed". Surface the
      // generic copy + offer the resend path.
      setStage({
        kind: 'error',
        error:
          err instanceof ApiError
            ? err
            : "That code didn't work. Please request a fresh code and try again.",
      });
    }
  }

  if (stage.kind === 'loading') {
    return (
      <div className="me-pending-ack-flow" aria-busy="true">
        <p>Opening the policy…</p>
      </div>
    );
  }
  if (stage.kind === 'mismatch') {
    return (
      <div className="me-pending-ack-flow me-pending-ack-mismatch" role="alert">
        <h4>We couldn&apos;t match that name</h4>
        <p>
          The name you typed doesn&apos;t match what we have on file, and it wasn&apos;t close
          enough for us to accept automatically. Please contact your compliance admin — they can
          either correct the name on your record or arrange a wet-signature acknowledgement for you.
        </p>
        <button type="button" className="me-pending-ack-close" onClick={onClose}>
          Close
        </button>
      </div>
    );
  }
  if (stage.kind === 'error') {
    return (
      <div className="me-pending-ack-flow me-pending-ack-error">
        <ErrorBanner error={stage.error} />
        <button type="button" className="me-pending-ack-close" onClick={onClose}>
          Close
        </button>
      </div>
    );
  }

  const page = stage.page;

  if (stage.kind === 'enter-code' || stage.kind === 'confirming') {
    return (
      <div className="me-pending-ack-flow me-pending-ack-flow--code">
        <h4>Enter your confirmation code</h4>
        <p>
          We just emailed a 6-digit confirmation code to your address. The code expires in{' '}
          {Math.max(1, Math.round(stage.kind === 'enter-code' ? stage.expiresInSeconds / 60 : 5))}{' '}
          minutes.
        </p>
        <form
          className="me-pending-ack-code-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (stage.kind === 'enter-code' && /^\d{6}$/.test(code.trim())) {
              void handleConfirm(stage.page, stage.attemptId);
            }
          }}
          noValidate
        >
          <label htmlFor="me-pending-ack-code-input">Confirmation code</label>
          <input
            id="me-pending-ack-code-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            disabled={stage.kind === 'confirming'}
            data-testid="me-pending-ack-code-input"
          />
          <button
            type="submit"
            className="me-pending-ack-submit-code"
            disabled={stage.kind === 'confirming' || !/^\d{6}$/.test(code.trim())}
            data-testid="me-pending-ack-confirm-button"
          >
            {stage.kind === 'confirming' ? 'Recording your acknowledgement…' : 'Confirm'}
          </button>
        </form>
        <button
          type="button"
          className="me-pending-ack-resend"
          onClick={() => void handleStepUp(page, false)}
          disabled={stage.kind === 'confirming'}
        >
          Send a fresh code
        </button>
        <button type="button" className="me-pending-ack-close" onClick={onClose}>
          Cancel
        </button>
      </div>
    );
  }

  // 'read', 'submitting', or 'near-match'
  return (
    <ReadAndStepUpPanel
      page={page}
      typedName={typedName}
      onTypedNameChange={setTypedName}
      readToEnd={readToEnd}
      onReadToEndChange={setReadToEnd}
      understandsLegal={understandsLegal}
      onUnderstandsLegalChange={setUnderstandsLegal}
      onReadViaExternal={() => setReadVia('external_viewer')}
      pending={stage.kind === 'submitting'}
      nearMatch={
        stage.kind === 'near-match' ? { typedName: stage.typedName, hint: stage.hint } : null
      }
      onSubmit={(override) => {
        void handleStepUp(page, override);
      }}
      onCancel={onClose}
    />
  );
}

interface ReadAndStepUpPanelProps {
  page: MeAckStartResponse;
  typedName: string;
  onTypedNameChange: (value: string) => void;
  readToEnd: boolean;
  onReadToEndChange: (value: boolean) => void;
  understandsLegal: boolean;
  onUnderstandsLegalChange: (value: boolean) => void;
  onReadViaExternal: () => void;
  pending: boolean;
  nearMatch: { typedName: string; hint: string } | null;
  onSubmit: (nameOverride: boolean) => void;
  onCancel: () => void;
}

function ReadAndStepUpPanel({
  page,
  typedName,
  onTypedNameChange,
  readToEnd,
  onReadToEndChange,
  understandsLegal,
  onUnderstandsLegalChange,
  onReadViaExternal,
  pending,
  nearMatch,
  onSubmit,
  onCancel,
}: ReadAndStepUpPanelProps): JSX.Element {
  const livePreview = useMemo(
    () => interpolateAckStatement(page.acknowledgement_text, typedName.trim()),
    [page.acknowledgement_text, typedName],
  );
  const trimmedName = typedName.trim();
  const canSubmit = Boolean(trimmedName) && readToEnd && understandsLegal && !pending;
  const isNearMatchPrompt = nearMatch !== null && nearMatch.typedName === trimmedName;

  return (
    <div className="me-pending-ack-flow me-pending-ack-flow--read">
      <h4>Acknowledge the {page.document_title}</h4>
      <p className="me-pending-ack-meta">Version {page.version}</p>
      <section aria-label="Policy document" className="me-pending-ack-pdf-section">
        <iframe
          className="me-pending-ack-pdf-frame"
          title={`${page.document_title} — version ${page.version}`}
          src={page.pdf_url}
        />
        <p className="me-pending-ack-pdf-fallback">
          Having trouble viewing the policy?{' '}
          <a
            href={page.pdf_url}
            target="_blank"
            rel="noreferrer noopener"
            onClick={onReadViaExternal}
            data-testid="me-pending-ack-external-viewer-link"
          >
            Open it in a new tab
          </a>
          .
        </p>
      </section>
      <form
        className="me-pending-ack-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) onSubmit(isNearMatchPrompt);
        }}
        noValidate
      >
        <label htmlFor="me-pending-ack-typed-name">
          Type your full name exactly as it appears on your ID
        </label>
        <input
          id="me-pending-ack-typed-name"
          type="text"
          autoComplete="name"
          spellCheck={false}
          value={typedName}
          onChange={(e) => onTypedNameChange(e.target.value)}
          disabled={pending}
        />
        <p className="me-pending-ack-name-on-file">
          On file as: <strong>{page.full_name_on_file}</strong>
        </p>
        <p className="me-pending-ack-live-preview" data-testid="me-pending-ack-live-preview">
          {livePreview}
        </p>
        <fieldset className="me-pending-ack-confirmations">
          <legend>Before you sign</legend>
          <label className="me-pending-ack-checkbox">
            <input
              type="checkbox"
              checked={readToEnd}
              onChange={(e) => onReadToEndChange(e.target.checked)}
              disabled={pending}
            />
            <span>
              I have read the {page.document_title} to the end and understand what it asks of me.
            </span>
          </label>
          <label className="me-pending-ack-checkbox">
            <input
              type="checkbox"
              checked={understandsLegal}
              onChange={(e) => onUnderstandsLegalChange(e.target.checked)}
              disabled={pending}
            />
            <span>
              I understand that typing my name here is legally equivalent to signing a paper copy.
            </span>
          </label>
        </fieldset>
        {isNearMatchPrompt ? (
          <div className="me-pending-ack-near-match" role="alert">
            <p>{nearMatch!.hint}</p>
            <p>
              If this is how your name appears on your ID, click Send confirmation code again.
              Otherwise, edit the name above.
            </p>
          </div>
        ) : null}
        <button
          type="submit"
          className="me-pending-ack-step-up"
          disabled={!canSubmit}
          data-testid="me-pending-ack-step-up-button"
        >
          {pending
            ? 'Sending confirmation code…'
            : isNearMatchPrompt
              ? 'Send confirmation code anyway'
              : 'Send confirmation code'}
        </button>
        <button type="button" className="me-pending-ack-close" onClick={onCancel}>
          Cancel
        </button>
      </form>
    </div>
  );
}

/**
 * Best-effort live preview of the rendered statement with the currently
 * typed name spliced in. The server is the authority on what gets stored
 * — this only changes what the volunteer sees on screen as they type.
 */
function interpolateAckStatement(serverText: string, typedName: string): string {
  if (!typedName) return serverText;
  const firstComma = serverText.indexOf(',');
  if (firstComma === -1) return serverText;
  return `${typedName}${serverText.slice(firstComma)}`;
}
